import { KeyRound } from "lucide-react";
import { Link } from "react-router-dom";
import type { ExpiringCredential } from "../types";
import {
  Card,
  CardHeader,
  Empty,
  formatDateShort,
  SkeletonRow,
  ViewAllLink,
} from "./_shared";

type Props = {
  credentials: ExpiringCredential[];
  isLoading?: boolean;
};

function expiryColor(iso: string | null): string {
  if (!iso) return "hsl(215,16%,47%)";
  const diff = new Date(iso).getTime() - Date.now();
  if (diff < 0) return "hsl(0,70%,40%)";
  const diffDays = diff / (24 * 3600 * 1000);
  if (diffDays < 7) return "hsl(0,70%,40%)";
  return "hsl(35,90%,35%)";
}

const SENSITIVITY_BG: Record<string, { bg: string; fg: string }> = {
  critical: { bg: "hsl(0,70%,92%)", fg: "hsl(0,70%,40%)" },
  high: { bg: "hsl(22,90%,92%)", fg: "hsl(22,90%,38%)" },
  medium: { bg: "hsl(38,90%,92%)", fg: "hsl(35,90%,32%)" },
  low: { bg: "hsl(210,16%,93%)", fg: "hsl(215,16%,40%)" },
};

export function ExpiringCredentialsWidget({
  credentials,
  isLoading,
}: Props) {
  return (
    <Card>
      <CardHeader
        icon={
          <KeyRound
            className="h-4 w-4 text-[hsl(0,70%,45%)]"
            strokeWidth={1.5}
          />
        }
        title="Expiring Credentials"
        count={credentials.length}
        accent={credentials.length > 0 ? "hsl(0,70%,45%)" : undefined}
        action={<ViewAllLink href="/access-vault" />}
      />
      {isLoading ? (
        <div>
          {[0, 1, 2].map((i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      ) : credentials.length === 0 ? (
        <Empty
          icon={
            <KeyRound
              className="h-8 w-8 text-muted-foreground opacity-40"
              strokeWidth={1.5}
            />
          }
          text="No credentials expiring soon"
          variant="success"
        />
      ) : (
        <ul className="divide-y" style={{ borderColor: "hsl(210,30%,92%)" }}>
          {credentials.slice(0, 5).map((c) => {
            const pal =
              SENSITIVITY_BG[c.sensitivity] ?? SENSITIVITY_BG.medium;
            return (
              <li key={c.id}>
                <Link
                  to={`/access-vault/${c.id}`}
                  className="flex items-center gap-2 py-2 hover:bg-[hsl(210,50%,98%)]"
                >
                  <span
                    className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium capitalize"
                    style={{ background: pal.bg, color: pal.fg }}
                  >
                    {c.sensitivity}
                  </span>
                  <span className="truncate text-[13px] font-medium text-foreground">
                    {c.title}
                  </span>
                  <span
                    className="ml-auto shrink-0 text-[11px] font-medium"
                    style={{ color: expiryColor(c.expiryDate) }}
                  >
                    {formatDateShort(c.expiryDate)}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}
